import { useEffect, useReducer } from 'react';

// Form state type
export interface EnterpriseSettingsFormState {
	companyName: string;
	logoPreview: string | null;
	addressStreet: string;
	addressCity: string;
	addressState: string;
	addressZip: string;
	addressCountry: string;
	contactPhone: string;
	contactEmail: string;
	contactWebsite: string;
	hasChanges: boolean;
	logoFile: File | null;
}

// Action types
export type EnterpriseSettingsFormAction =
	| { type: 'SET_FIELD'; field: keyof EnterpriseSettingsFormState; value: any }
	| { type: 'SET_LOGO'; file: File; preview: string }
	| { type: 'LOAD_SETTINGS'; settings: any }
	| { type: 'RESET_CHANGES' };

// Initial state
const initialState: EnterpriseSettingsFormState = {
	companyName: '',
	logoPreview: null,
	addressStreet: '',
	addressCity: '',
	addressState: '',
	addressZip: '',
	addressCountry: 'USA',
	contactPhone: '',
	contactEmail: '',
	contactWebsite: '',
	hasChanges: false,
	logoFile: null,
};

// Reducer function
function enterpriseSettingsFormReducer(
	state: EnterpriseSettingsFormState,
	action: EnterpriseSettingsFormAction,
): EnterpriseSettingsFormState {
	switch (action.type) {
		case 'SET_FIELD':
			return {
				...state,
				[action.field]: action.value,
				hasChanges: true,
			};
		case 'SET_LOGO':
			return {
				...state,
				logoFile: action.file,
				logoPreview: action.preview,
				hasChanges: true,
			};
		case 'LOAD_SETTINGS':
			return {
				...state,
				companyName: action.settings.company_name || '',
				addressStreet: action.settings.address?.street || '',
				addressCity: action.settings.address?.city || '',
				addressState: action.settings.address?.state || '',
				addressZip: action.settings.address?.zip || '',
				addressCountry: action.settings.address?.country || 'USA',
				contactPhone: action.settings.contact?.phone || '',
				contactEmail: action.settings.contact?.email || '',
				contactWebsite: action.settings.contact?.website || '',
				logoPreview: action.settings.logo_base64
					? `data:image/png;base64,${action.settings.logo_base64}`
					: null,
				hasChanges: false,
			};
		case 'RESET_CHANGES':
			return {
				...state,
				hasChanges: false,
				logoFile: null,
			};
		default:
			return state;
	}
}

/**
 * Custom hook for managing enterprise settings form state
 * @param settings - Initial settings data from API
 * @returns Tuple of [state, dispatch]
 */
export function useEnterpriseSettingsForm(settings: any) {
	const [state, dispatch] = useReducer(
		enterpriseSettingsFormReducer,
		initialState,
	);

	// Load settings when they change
	useEffect(() => {
		if (settings) {
			dispatch({ type: 'LOAD_SETTINGS', settings });
		}
	}, [settings]);

	return [state, dispatch] as const;
}
